/**
 * Linear Algebra Kingdom, surveyed: the grid city, and the inside of all five
 * of its regions (docs/rpg.md §22, `.claude/skills/world-maps/SKILL.md`).
 *
 * **The streets meet at right angles, and no other kingdom's do.** Everywhere
 * else in this world the lanes wander — a mill stream decides where Starter
 * Village's bridge goes, a switchback decides the trigonometry. Here the city
 * is laid out on axes and the blocks between the streets are a grid, because
 * that is what this kingdom's mathematics is: arrows measured along directions
 * you chose, and grids of numbers that move them. A matrix is the one object in
 * the curriculum a tile map can draw exactly rather than allude to.
 *
 * **It is a coast, not a col.** Nothing in linear algebra depends on a limit, a
 * derivative or an integral (`content/src/linear-algebra.test.ts` walks the
 * prerequisites to prove it), so this city has a harbour and a road back to the
 * school algebra it came from, and no pass leading up into the calculus. A
 * student heading for engineering takes both roads, in either order, and the
 * map should not imply one of them is on the way to the other.
 *
 * **Terrain is scenery and never a lock.** A grid is walls, and walls seal
 * things; every block here has its doorways cut and `validateRegionMap` walks
 * all six from their arrival tiles on every `npm run content:validate`.
 */

import type { RegionMap, Town } from '@yuan-math/world';

const KINGDOM = 'kingdom-linear-algebra';

/* ========================================================== the grid city === */

/**
 * The city: four streets north to south, three east to west, water at the foot.
 *
 * The archives are in the block under the crag, the foundry and the halls face
 * each other across the middle street, and the spire stands on the rock in the
 * north-west corner where the city runs out. The quayside runs the whole width
 * at the bottom, because the harbour is the one part of this kingdom that is
 * not on the grid — an arrow does not have to point along a street.
 */
const LINEAR_ALGEBRA_CITY: Town = {
  kingdomId: KINGDOM,
  caption: 'A city laid out on axes: four streets one way, three the other, the works and the halls facing across the middle, and the water at the foot of it.',
  width: 26,
  height: 18,
  /* In the middle of a block, off the streets, with the foundry and the halls
     both in sight along the middle street. */
  start: { x: 7, y: 9 },
  rows: [
    'f^^^^^^^^^^^^^^^^^^^^^^^^f',
    'f^^^..%%%%#.....#.....#..f',
    'f^^^..%__%#.....#.....#..f',
    'f^^^..%_%%#.....#.....#..f',
    'f########################f',
    'f...#.....#%%%%%#%%%%%#..f',
    'f...#.....#%___%#%___%#..f',
    'f...#.....#%%_%%#%%_%%#..f',
    'f########################f',
    'f...#.....#fffff#ffff.#..f',
    'f...#.....#fffff#ffff.#..f',
    'f...#.....#fffff#ffff.#..f',
    'f########################f',
    'f,,,,,,,,,,,,,,,,,,,,,,,,f',
    'f~~~~~~~~~~~~~~~~~~~~~~~~f',
    'f~~~~~~~~~~~~~~~~~~~~~~~~f',
    'f~~~~~~~~~~~~~~~~~~~~~~~~f',
    'ffffffffffffffffffffffffff',
  ],
  places: [
    { kind: 'road', name: 'The Road Out', emoji: '🗺️', x: 1, y: 12 },
    { kind: 'quests', name: 'The Notice Board', emoji: '📜', x: 5, y: 12 },
    { kind: 'region', regionId: 'region-vector-harbour', x: 13, y: 13 },
    { kind: 'region', regionId: 'region-matrix-foundry', x: 13, y: 8 },
    { kind: 'region', regionId: 'region-system-halls', x: 19, y: 8 },
    { kind: 'region', regionId: 'region-vector-space-archives', x: 7, y: 4 },
    { kind: 'region', regionId: 'region-eigen-spire', x: 22, y: 1 },
    { kind: 'boss', bossId: 'boss-linear-algebra-kingdom', x: 10, y: 12 },
  ],
};

/* =========================================================== the regions === */

/**
 * Vector Harbour — jetties off one quay, each going its own way.
 *
 * Every jetty starts at the same quay and none of them runs parallel to
 * another, which is the whole of what an arrow is for here. Two of them turn
 * partway along, and the two that turn are where the combinations and the span
 * stand: what you can reach by going along one and then the other. The
 * independence post is on the jetty that shares its direction with nothing.
 */
const VECTOR_HARBOUR: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-vector-harbour',
  caption: 'One quay, five jetties, and no two of them pointing the same way.',
  width: 19,
  height: 13,
  /* On the shingle below the quay, looking out along the jetties. */
  start: { x: 9, y: 10 },
  rows: [
    '~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~~~~~~~~~~~~',
    '~~~~~~~~#~~~~~~~~~~',
    '~~~~###~#~~~~~~~~~~',
    '~~~~#~~~#~~~###~~~~',
    '~~~~#~~~#~~~#~~#~~~',
    '~~~~#~~~#~~~#~~#~~~',
    '~~###############~~',
    '~,,,,,,,,,,,,,,,,,~',
    '~,,,,,,,,,,,,,,,,,~',
    '~.................~',
    '~~~~~~~~~.~~~~~~~~~',
  ],
  places: [
    { kind: 'concept', conceptId: 'vectors', x: 8, y: 3 },
    { kind: 'concept', conceptId: 'vectors-rn', x: 6, y: 4 },
    { kind: 'concept', conceptId: 'dot-product', x: 14, y: 5 },
    { kind: 'concept', conceptId: 'span', x: 15, y: 6 },
    { kind: 'concept', conceptId: 'linear-combinations', x: 12, y: 6 },
    { kind: 'concept', conceptId: 'linear-independence', x: 4, y: 6 },
    { kind: 'road', name: 'The Harbour Road', emoji: '🧭', x: 9, y: 12 },
  ],
};

/**
 * Matrix Foundry — casting frames in rows and columns.
 *
 * The floor is a grid with a doorway cut in every wall, so it can be walked
 * along a row or down a column and reads the same either way, which is the
 * thing about a matrix that a list of numbers hides. The elimination bench is
 * in the middle of a row because that is where the work happens; the
 * determinant is off in the corner frame, being one number about the whole
 * plot rather than anything you do to a row.
 */
const MATRIX_FOUNDRY: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-matrix-foundry',
  caption: 'Casting frames in rows and columns, with a doorway through every wall, so the floor walks the same way along as it does down.',
  width: 19,
  height: 13,
  /* In an aisle between frames, one row up from the door. */
  start: { x: 13, y: 11 },
  rows: [
    '%%%%%%%%%%%%%%%%%%%',
    '%___%___%___%_____%',
    '%___%___%___%_____%',
    '%%_%%%_%%%_%%%%_%%%',
    '%_________________%',
    '%___%___%___%_____%',
    '%%_%%%_%%%_%%%%_%%%',
    '%_________________%',
    '%___%___%___%_____%',
    '%%_%%%_%%%_%%%%_%%%',
    '%_________________%',
    '%___%___%___%_____%',
    '%%%%%%%%%_%%%%%%%%%',
  ],
  places: [
    { kind: 'concept', conceptId: 'matrices', x: 2, y: 2 },
    { kind: 'concept', conceptId: 'row-reduction', x: 6, y: 2 },
    { kind: 'concept', conceptId: 'gaussian-elimination', x: 10, y: 5 },
    { kind: 'concept', conceptId: 'matrix-inverse', x: 15, y: 5 },
    { kind: 'concept', conceptId: 'determinants', x: 6, y: 8 },
    { kind: 'road', name: 'The Works Door', emoji: '🧭', x: 9, y: 12 },
  ],
};

/**
 * System Halls — three halls off one long room.
 *
 * Two halls with a system in each and the rank between them, because rank is
 * what you find out by putting the two side by side. The long room below runs
 * the width of the building: a solution set is a shape rather than a point, and
 * the one part of this map with no walls across it is where that is said.
 */
const SYSTEM_HALLS: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-system-halls',
  caption: 'Two halls with a system in each, the rank between them, and one long room underneath with nothing partitioning it.',
  width: 17,
  height: 12,
  /* In the long room, below the halls. */
  start: { x: 8, y: 9 },
  rows: [
    '%%%%%%%%%%%%%%%%%',
    '%____%_____%____%',
    '%____%_____%____%',
    '%____%_____%____%',
    '%____%_____%____%',
    '%_______________%',
    '%____%_____%____%',
    '%____%_____%____%',
    '%%%%%%%%_%%%%%%%%',
    '%_______________%',
    '%_______________%',
    '%%%%%%%%_%%%%%%%%',
  ],
  places: [
    { kind: 'concept', conceptId: 'linear-systems', x: 3, y: 3 },
    { kind: 'concept', conceptId: 'solution-sets', x: 8, y: 3 },
    { kind: 'concept', conceptId: 'rank', x: 13, y: 3 },
    { kind: 'road', name: 'The Hall Door', emoji: '🧭', x: 8, y: 11 },
  ],
};

/**
 * Vector Space Archives — four rooms standing inside one room.
 *
 * A subspace is a space inside a space, and that is a thing a floor plan can
 * simply be rather than describe: the great room holds four smaller ones, each
 * with its own door, and the space itself is named out on the open floor that
 * contains all of them. The transformations are in the middle where the four
 * rooms meet, since that is what carries one into another.
 */
const VECTOR_SPACE_ARCHIVES: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-vector-space-archives',
  caption: 'One great room with four smaller rooms standing inside it, each with its own door onto the floor that holds them all.',
  width: 20,
  height: 15,
  /* On the open floor inside the door, with all four rooms in front of you. */
  start: { x: 10, y: 13 },
  rows: [
    '%%%%%%%%%%%%%%%%%%%%',
    '%__________________%',
    '%__________________%',
    '%__%%%%%%%__%%%%%%_%',
    '%__%_____%__%____%_%',
    '%__%_____%__%____%_%',
    '%__%_____%__%____%_%',
    '%__%%%_%%%__%%_%%%_%',
    '%__________________%',
    '%__%%%_%%%__%%_%%%_%',
    '%__%_____%__%____%_%',
    '%__%_____%__%____%_%',
    '%__%%%%%%%__%%%%%%_%',
    '%__________________%',
    '%%%%%%%%%%_%%%%%%%%%',
  ],
  places: [
    { kind: 'concept', conceptId: 'vector-spaces', x: 10, y: 2 },
    { kind: 'concept', conceptId: 'subspaces', x: 6, y: 5 },
    { kind: 'concept', conceptId: 'basis', x: 14, y: 5 },
    { kind: 'concept', conceptId: 'dimension', x: 6, y: 10 },
    { kind: 'concept', conceptId: 'coordinates', x: 14, y: 10 },
    { kind: 'concept', conceptId: 'linear-transformations', x: 10, y: 8 },
    { kind: 'concept', conceptId: 'kernel-range', x: 2, y: 12 },
    { kind: 'concept', conceptId: 'transformation-matrix', x: 18, y: 12 },
    { kind: 'road', name: 'The Archive Door', emoji: '🧭', x: 10, y: 14 },
  ],
};

/**
 * Eigen Spire — one shaft, straight up, that never turns.
 *
 * Four floors and a stair that goes through all of them without once changing
 * direction, which is the only thing an eigenvector is: the direction a
 * transformation leaves alone. Everything on the way up is off the shaft, and
 * the shaft is the same line on every floor.
 */
const EIGEN_SPIRE: RegionMap = {
  kingdomId: KINGDOM,
  regionId: 'region-eigen-spire',
  caption: 'Four floors and one shaft straight up through all of them, never once turning.',
  width: 17,
  height: 14,
  /* On the ground floor, beside the foot of the shaft. */
  start: { x: 9, y: 11 },
  rows: [
    '^^^^^^^^^^^^^^^^^',
    '^^^^^%%%%%%%^^^^^',
    '^^^^^%_____%^^^^^',
    '^^^^^%_____%^^^^^',
    '^^^^^%%%_%%%^^^^^',
    '^^^^^%_____%^^^^^',
    '^^^^^%_____%^^^^^',
    '^^^^^%%%_%%%^^^^^',
    '^^^^^%_____%^^^^^',
    '^^^^^%_____%^^^^^',
    '^^^^^%%%_%%%^^^^^',
    '^^^^^%_____%^^^^^',
    '^...............^',
    '^^^^^^^^.^^^^^^^^',
  ],
  places: [
    { kind: 'concept', conceptId: 'eigen-applications', x: 7, y: 11 },
    { kind: 'concept', conceptId: 'eigenvectors', x: 7, y: 8 },
    { kind: 'concept', conceptId: 'eigenvalues', x: 7, y: 5 },
    { kind: 'concept', conceptId: 'diagonalization', x: 9, y: 2 },
    { kind: 'road', name: 'The Spire Steps', emoji: '🧭', x: 8, y: 13 },
  ],
};

/* -------------------------------------------------------------------------- */

export const LINEAR_ALGEBRA_TOWN: Town = LINEAR_ALGEBRA_CITY;

export const LINEAR_ALGEBRA_REGION_MAPS: readonly RegionMap[] = [
  VECTOR_HARBOUR,
  MATRIX_FOUNDRY,
  SYSTEM_HALLS,
  VECTOR_SPACE_ARCHIVES,
  EIGEN_SPIRE,
];
